import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Grid,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  Divider,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  DirectionsCar as CarIcon,
  EmojiObjects as MissionIcon,
  Groups as TeamIcon,
  Handshake as ValueIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const AboutPage: React.FC = () => {
  const navigate = useNavigate();

  React.useEffect(() => {
    document.title = "About Us - adiLessons";
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Learn more about adiLessons. Our mission is to empower UK Approved Driving Instructors (ADIs) with diary management and automation software.');
  }, []);

  const values = [
    {
      icon: <MissionIcon sx={{ fontSize: 32, color: '#1967D2' }} />,
      title: 'Our Mission',
      description: 'To save independent UK driving instructors and driving schools up to 10 hours of manual administration weekly, allowing them to focus on high-quality driver training.'
    },
    {
      icon: <ValueIcon sx={{ fontSize: 32, color: '#25D366' }} />,
      title: 'Our Core Value',
      description: 'We believe in transparent, reliable tools. No hidden fees, no complicated setups—just robust automated WhatsApp dispatches and diary schedulers that run seamlessly on autopilot.'
    },
    {
      icon: <TeamIcon sx={{ fontSize: 32, color: '#ffb300' }} />,
      title: 'Built in the UK',
      description: 'adiLessons is designed specifically for the UK driving market, integrating the official DVSA training syllabus and regional search parameters to help instructors establish local brands.'
    }
  ];

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#f4f6f9' }}>
      {/* Header */}
      <AppBar
        position="sticky"
        sx={{
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.05)',
          color: '#2b3154',
          zIndex: 1100
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ display: 'flex', justifyContent: 'space-between', height: 72 }}>
            <Box display="flex" alignItems="center" gap={1} onClick={() => navigate('/')} sx={{ cursor: 'pointer' }}>
              <CarIcon sx={{ color: '#1967D2', fontSize: 28 }} />
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 'bold', 
                  color: '#2b3154', 
                  letterSpacing: '-0.5px',
                  fontFamily: '"Outfit", "Inter", sans-serif'
                }}
              >
                adi<span style={{ color: '#E53935', fontWeight: '900' }}>L</span>essons
              </Typography>
            </Box>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/')}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 'bold' }}
            >
              Back to Home
            </Button>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="md" sx={{ py: { xs: 6, md: 10 }, flexGrow: 1 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 4, md: 6 },
            borderRadius: 4,
            border: '1px solid #e2e8f0',
            boxShadow: '0 4px 24px rgba(0, 0, 0, 0.02)',
            bgcolor: 'white',
          }}
        >
          <Box sx={{ mb: 6, textAlign: 'center' }}>
            <Typography variant="h3" fontWeight="900" sx={{ mb: 2, color: '#0f162a', fontFamily: '"Outfit", sans-serif' }}>
              About adiLessons
            </Typography>
            <Typography variant="subtitle1" color="text.secondary" sx={{ fontSize: '18px', lineHeight: 1.6 }}>
              Empowering Approved Driving Instructors with modern tools to reclaim their time and build structured businesses.
            </Typography>
          </Box>

          <Typography variant="body1" color="text.secondary" paragraph sx={{ lineHeight: 1.8, fontSize: '16px', mb: 4 }}>
            In the UK, independent driving instructors lose hours every week manually replying to lesson booking messages, reminding pupils about schedules, chasing payments, and managing manual diaries. In addition, student cancellations and no-shows cost instructors hundreds of pounds in lost income monthly.
          </Typography>

          <Typography variant="body1" color="text.secondary" paragraph sx={{ lineHeight: 1.8, fontSize: '16px', mb: 6 }}>
            <strong>adiLessons</strong> was founded to solve this diary chaos. By developing a unified business platform with automated WhatsApp dispatches, 24/7 calendar schedulers, online payment interfaces, and DVSA progress maps, we help instructors run their businesses like a modern enterprise, keeping their cars moving and pupils passing.
          </Typography>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h5" fontWeight="bold" sx={{ color: '#0f162a', mb: 4, textAlign: 'center' }}>
            What We Stand For
          </Typography>

          <Grid container spacing={3}>
            {values.map((val, index) => (
              <Grid size={{ xs: 12 }} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    border: '1px solid #f1f5f9',
                    bgcolor: '#fafafa',
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    gap: 3
                  }}
                >
                  <Box sx={{ display: 'inline-flex', p: 2, borderRadius: '50%', bgcolor: '#ffffff', boxShadow: '0 2px 10px rgba(0,0,0,0.02)' }}>
                    {val.icon}
                  </Box>
                  <Box>
                    <Typography variant="h6" fontWeight="bold" sx={{ color: '#0f162a', mb: 0.5 }}>
                      {val.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                      {val.description}
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Container>

      {/* Footer */}
      <Box sx={{ py: 3, bgcolor: '#0d132a', color: '#90a4ae', textAlign: 'center', borderTop: '1px solid #1c2a4a', mt: 'auto' }}>
        <Typography variant="caption">
          © {new Date().getFullYear()} adiLessons. All rights reserved. adiLessons is operated under the brand name adiLessons. Contact: support@adilesson.co.uk.
        </Typography>
      </Box>
    </Box>
  );
};

export default AboutPage;
